import React, { useState, useEffect } from 'react';
import { History, RotateCcw, Eye, X } from 'lucide-react';
import { sans, mono, green, greenLight, useTheme } from '../../tokens';
import {
  getPublishHistory, publishSnapshot,
} from '../../data/investorSnapshots';

// Admin-only version history viewer. Lists every version that was ever
// published for the selected period, lets the admin preview any of them
// (read-only JSON detail), and restore / republish a prior one.
// Restoring creates a NEW version pointing at the old content (so history
// never loses entries).

export default function VersionHistory({ companyId, selectedPeriod, currentUser, onRestored }) {
  const t = useTheme();
  const [history, setHistory] = useState(() => getPublishHistory(companyId, selectedPeriod));
  const [previewIdx, setPreviewIdx] = useState(null);

  useEffect(() => setHistory(getPublishHistory(companyId, selectedPeriod)), [companyId, selectedPeriod]);

  const restore = (entry) => {
    // Create a brand-new version entry carrying the old data. History is
    // preserved — we never overwrite or delete prior versions.
    const snap = publishSnapshot(companyId, selectedPeriod, currentUser, entry.data);
    setHistory(getPublishHistory(companyId, selectedPeriod));
    onRestored && onRestored(snap);
  };

  if (history.length === 0) {
    return (
      <div style={{
        padding: 14, border: `1px dashed ${t.border}`, borderRadius: 8,
        fontSize: 11, color: t.textMuted, fontFamily: sans, textAlign: 'center',
      }}>
        No prior versions for this period.
      </div>
    );
  }

  return (
    <div style={{
      background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10,
      padding: '16px 20px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <History size={14} style={{ color: t.textMuted }} />
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontFamily: sans }}>
          Version history ({history.length})
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {history.map((entry, i) => {
          const isCurrent = i === 0;
          const d = new Date(entry.publishedAt);
          const dateLabel = d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' });
          return (
            <div key={entry.publishedAt + i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', borderRadius: 6,
              background: isCurrent ? greenLight : 'transparent',
              border: `1px solid ${isCurrent ? green : t.borderLight}`,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontFamily: sans, fontWeight: 600, color: t.textPrimary }}>
                  {dateLabel}
                  {isCurrent && (
                    <span style={{
                      marginLeft: 8, fontSize: 8, fontWeight: 700, textTransform: 'uppercase',
                      padding: '1px 6px', borderRadius: 8,
                      background: green, color: '#fff',
                    }}>Current</span>
                  )}
                </div>
                <div style={{ fontSize: 9, fontFamily: mono, color: t.textMuted, marginTop: 2 }}>
                  {entry.publishedBy || 'unknown'}
                </div>
              </div>
              <button onClick={() => setPreviewIdx(i)} title="Preview this version"
                style={{ background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 4, padding: '3px 8px', cursor: 'pointer', color: t.textMuted, display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontFamily: sans }}>
                <Eye size={11} /> Preview
              </button>
              {!isCurrent && (
                <button onClick={() => restore(entry)} title="Restore / republish this version"
                  style={{ background: green, border: 'none', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontFamily: sans, fontWeight: 600 }}>
                  <RotateCcw size={11} /> Restore
                </button>
              )}
            </div>
          );
        })}
      </div>

      {previewIdx !== null && history[previewIdx] && (
        <div
          onClick={() => setPreviewIdx(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10,
              padding: '18px 22px', width: 'min(640px, 92vw)', maxHeight: '80vh', overflow: 'auto',
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontFamily: sans }}>
                Version preview
              </div>
              <button onClick={() => setPreviewIdx(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 4 }}>
                <X size={14} />
              </button>
            </div>
            <div style={{ fontSize: 11, fontFamily: mono, color: t.textSecondary, marginBottom: 10 }}>
              {new Date(history[previewIdx].publishedAt).toLocaleString()} · {history[previewIdx].publishedBy || 'unknown'}
            </div>
            <pre style={{
              fontSize: 10, fontFamily: mono, background: t.bgSurface,
              padding: 12, borderRadius: 6, overflow: 'auto', color: t.textPrimary,
            }}>
{JSON.stringify(history[previewIdx].data, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
